/**
 * The four marks the design foundations settled on, and the pills that label a verdict.
 *
 * Each one exists because a plainer alternative would have said less: the fault strip keeps the
 * predicate's initial because which rule spoke is the whole finding, and the compass bar keeps
 * the chart row underneath the accepted span because the difference between them is the point.
 */

import type { ReactNode } from "react";
import type { RangeRow } from "@app/shared-types";
import { percent, rate } from "../lib/format";

type Tone = "clean" | "fault" | "silent" | "unread";

const TONES: Record<Tone, { pill: string; dot: string }> = {
  clean: { pill: "bg-[#E2EDE7] text-clean-strong", dot: "bg-clean" },
  fault: { pill: "bg-[#F5E3DD] text-fault-strong", dot: "bg-fault" },
  silent: { pill: "bg-track text-[#58544A]", dot: "bg-silent" },
  unread: { pill: "bg-departure text-departure-ink", dot: "bg-unread" },
};

export function Pill({ tone, children }: { tone: Tone; children: ReactNode }): JSX.Element {
  const look = TONES[tone];
  return (
    <span
      className={`inline-flex h-7 items-center gap-1.5 rounded-full px-3 text-xs font-semibold ${look.pill}`}
    >
      <span className={`h-[7px] w-[7px] rounded-full ${look.dot}`} aria-hidden="true" />
      {children}
    </span>
  );
}

/** The verdict on one score, said in as few words as it can be said. */
export function VerdictPill({ faults }: { faults: number | null }): JSX.Element {
  if (faults === null) return <Pill tone="unread">not judged</Pill>;
  if (faults === 0) return <Pill tone="clean">clean</Pill>;
  return (
    <Pill tone="fault">
      {faults} fault{faults === 1 ? "" : "s"}
    </Pill>
  );
}

/**
 * One predicate's rate as a bar. A predicate that judged nothing draws no bar at all — an empty
 * track and an em dash, never a full green one.
 */
export function RuleBar({
  name,
  score,
  detail,
}: {
  name: string;
  score: number | null;
  detail?: ReactNode;
}): JSX.Element {
  const width = percent(score);
  return (
    <div className="flex items-center gap-3 border-b border-rule-faint py-3 last:border-b-0">
      <span className="w-[84px] shrink-0 text-[13px] font-semibold">{name}</span>
      <span className="h-[5px] flex-1 overflow-hidden rounded-[3px] bg-rule-soft">
        {score === null ? null : (
          <span
            className={`block h-[5px] ${width >= 90 ? "bg-clean" : "bg-fault"}`}
            style={{ width: `${width}%` }}
          />
        )}
      </span>
      {detail ? <span className="text-xs text-ink-quiet">{detail}</span> : null}
      <span className="w-[52px] shrink-0 text-right font-mono text-[13px]">{rate(score)}</span>
    </div>
  );
}

/** One square per chord through a figure, red with the initial of the rule that spoke. */
export function FaultStrip({ marks }: { marks: string[] }): JSX.Element {
  return (
    <div className="flex flex-wrap gap-1">
      {marks.map((mark, index) => (
        <span
          key={index}
          title={mark ? mark : "clean"}
          className={`h-[15px] w-[15px] rounded-[3px] text-center font-mono text-[9.5px] leading-[15px] ${
            mark ? "bg-fault text-leaf" : "bg-track-deep"
          }`}
        >
          {mark}
        </span>
      ))}
    </div>
  );
}

/**
 * An instrument's compass. The pale segment is the chart row as engraved; the dark one is the
 * scope of greatest expression where the book marks one, and the practical top otherwise. A
 * span the verifier accepts beyond the engraving — three rows do — shows as a hairline outside
 * the pale block, because that difference is exactly what the compass rule is arguing about.
 * Divisions are one octave apart.
 */
export function CompassBar({ row }: { row: RangeRow }): JSX.Element {
  const low = Math.min(row.chart.low, row.accepted.low);
  const high = Math.max(row.chart.high, row.accepted.high);
  const span = Math.max(high - low, 1);
  const place = (from: number, to: number) => ({
    left: `${((from - low) / span) * 100}%`,
    width: `${((to - from) / span) * 100}%`,
  });
  const dark =
    row.registers.find((one) => one.name === "expression") ??
    row.registers.find((one) => one.name === "practical");
  const octaves: number[] = [];
  for (let midi = Math.ceil(low / 12) * 12; midi < high; midi += 12) octaves.push(midi);

  return (
    <div className="relative h-[7px] w-full rounded bg-track-deep">
      <span
        className="absolute inset-y-0 rounded bg-[#B5AD9A]"
        style={place(row.chart.low, row.chart.high)}
      />
      {dark ? (
        <span className="absolute inset-y-0 rounded bg-clean" style={place(dark.low, dark.high)} />
      ) : null}
      {octaves.map((midi) => (
        <span
          key={midi}
          className="absolute inset-y-0 w-px bg-paper/70"
          style={{ left: `${((midi - low) / span) * 100}%` }}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}
